import {
  getSearchHitPageLabel,
  resolveContentCategory,
} from "../utils/searchPageLabel";
import { resolveSearchHitUrl } from "../utils/searchHitUrl";
import {
  buildNewsListLookups,
  isNewsSearchHit,
  resolveNewsHitUrl,
} from "../utils/searchNewsUrl";

const apiBase = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const token = import.meta.env.VITE_STRAPI_TOKEN;

const DEFAULT_SEARCH_LIMIT = 100;
const FALLBACK_PER_TYPE_LIMIT = 30;

const SEARCH_CATEGORY_ORDER = {
  product: 0,
  download: 1,
  support: 2,
  news: 3,
};

const CAS_SEARCH_CONTENT_TYPES = [
  "api::cas-product-list.cas-product-list",
  "api::cas-sensaphone-item.cas-sensaphone-item",
  "api::cas-raychem-item.cas-raychem-item",
  "api::cas-sensaphone-accessories-item.cas-sensaphone-accessories-item",
  "api::cas-product-category.cas-product-category",
  "api::cas-trace-tek-category.cas-trace-tek-category",
  "api::cas-download.cas-download",
  "api::cas-support-category.cas-support-category",
  "api::cas-new.cas-new",
  "api::cas-newsletter.cas-newsletter",
];

const CONTENT_TYPE_UID_LABELS = {
  "api::cas-new.cas-new": "news",
  "api::cas-newsletter.cas-newsletter": "news",
  "api::cas-product-list.cas-product-list": "product",
  "api::cas-sensaphone-item.cas-sensaphone-item": "product",
  "api::cas-raychem-item.cas-raychem-item": "product",
  "api::cas-download.cas-download": "download",
  "api::cas-product-category.cas-product-category": "product",
  "api::cas-support-category.cas-support-category": "support",
  "api::cas-sensaphone-accessories-item.cas-sensaphone-accessories-item": "product",
  "api::cas-trace-tek-category.cas-trace-tek-category": "product",
};

function searchHeaders() {
  const headers = { "Content-Type": "application/json" };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function parseSearchResponse(res) {
  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const message =
      payload?.error?.message ||
      payload?.message ||
      `Search failed (${res.status})`;
    throw new Error(message);
  }

  return payload;
}

function mapContentTypeLabel(contentTypeUid) {
  if (!contentTypeUid) return "content";
  if (CONTENT_TYPE_UID_LABELS[contentTypeUid]) {
    return CONTENT_TYPE_UID_LABELS[contentTypeUid];
  }
  const shortName = String(contentTypeUid).split(".").pop() || contentTypeUid;
  return shortName.replace(/^cas-/, "").replace(/-/g, " ");
}

/** Prefer the hit's indexed content type over the search query content type. */
function resolveHitContentTypeUid(hit, fallbackContentType) {
  const hitType = hit?.contentType;
  if (!hitType) {
    return fallbackContentType || "";
  }

  if (String(hitType).startsWith("api::")) {
    return hitType;
  }

  const shortName = String(hitType);
  const matchedUid = CAS_SEARCH_CONTENT_TYPES.find((uid) => {
    const name = uid.split(".")[1] || "";
    return name === `cas-${shortName}` || name === shortName;
  });

  return matchedUid || shortName;
}

function normalizeHit(hit, fallbackContentType) {
  const contentTypeUid = resolveHitContentTypeUid(hit, fallbackContentType);
  const contentType =
    hit.contentType ||
    mapContentTypeLabel(contentTypeUid) ||
    mapContentTypeLabel(fallbackContentType) ||
    "content";
  const url = resolveSearchHitUrl(hit, contentTypeUid) || "";

  return {
    ...hit,
    title: hit.title || hit.name || "Untitled",
    contentType,
    url,
    pageLabel: getSearchHitPageLabel({
      url,
      contentType: contentTypeUid,
    }),
  };
}

async function fetchNewsListLookups(locale) {
  try {
    const query = new URLSearchParams({
      locale,
      "populate[cas_news][populate][list][populate]": "*",
    });
    const res = await fetch(`${apiBase}/cas-news-page?${query}`, {
      headers: searchHeaders(),
    });
    const payload = await parseSearchResponse(res);
    const list =
      payload?.data?.cas_news?.list ||
      payload?.data?.cas_news?.[0]?.list ||
      [];
    return buildNewsListLookups(list);
  } catch {
    return null;
  }
}

async function enrichNewsHitUrls(hits, locale) {
  const hasNewsHits = hits.some(isNewsSearchHit);
  if (!hasNewsHits) return hits;

  const lookups = await fetchNewsListLookups(locale);
  if (!lookups) return hits;

  return hits.map((hit) => {
    if (!isNewsSearchHit(hit)) return hit;

    const resolvedUrl = resolveNewsHitUrl(hit, lookups);
    if (!resolvedUrl) return hit;

    return {
      ...hit,
      url: resolvedUrl,
      pageLabel: getSearchHitPageLabel({
        url: resolvedUrl,
        contentType: hit.contentType,
      }),
    };
  });
}

function isAccessorySearchHit(hit) {
  const type = String(hit?.contentType || "").toLowerCase();
  return type === "sensaphone-accessories-item";
}

function hitHasUrl(hit) {
  return Boolean(hit?.url && String(hit.url).trim());
}

let accessoriesUrlMapCache = { locale: null, map: null };

async function fetchAccessoriesCategoryUrlMap(locale) {
  if (accessoriesUrlMapCache.locale === locale && accessoriesUrlMapCache.map) {
    return accessoriesUrlMapCache.map;
  }

  try {
    const query = new URLSearchParams({
      locale,
      "populate[cas_sensaphone_accessories_items][fields][0]": "documentId",
      "fields[0]": "url",
    });
    const res = await fetch(
      `${apiBase}/cas-sensaphone-accessories-categories?${query}`,
      { headers: searchHeaders() }
    );
    const payload = await parseSearchResponse(res);
    const map = new Map();
    const categories = Array.isArray(payload?.data) ? payload.data : [];

    for (const category of categories) {
      const url = category?.url;
      if (!url) continue;

      const items = category.cas_sensaphone_accessories_items;
      const itemList = Array.isArray(items) ? items : [];

      for (const item of itemList) {
        const docId = item?.documentId;
        if (docId) {
          map.set(docId, url);
        }
      }
    }

    accessoriesUrlMapCache = { locale, map };
    return map;
  } catch {
    return null;
  }
}

async function enrichAccessoryHitUrls(hits, locale) {
  const needsEnrichment = hits.some(
    (hit) => isAccessorySearchHit(hit) && !hitHasUrl(hit)
  );
  if (!needsEnrichment) return hits;

  const map = await fetchAccessoriesCategoryUrlMap(locale);
  if (!map) return hits;

  return hits.map((hit) => {
    if (!isAccessorySearchHit(hit) || hitHasUrl(hit)) return hit;

    const docId = hit.documentId || hit.parentDocumentId;
    const resolvedUrl = docId ? map.get(String(docId)) : null;
    if (!resolvedUrl) return hit;

    return {
      ...hit,
      url: resolvedUrl,
      pageLabel: getSearchHitPageLabel({
        url: resolvedUrl,
        contentType: hit.contentType,
      }),
    };
  });
}

async function enrichSearchHitUrls(hits, locale) {
  const withNewsUrls = await enrichNewsHitUrls(hits, locale);
  return enrichAccessoryHitUrls(withNewsUrls, locale);
}

/**
 * Normalize single-search flat hits or search-all grouped hits.
 */
export function normalizeSearchHits(response, fallbackContentType) {
  const data = response?.data;
  if (!Array.isArray(data)) return [];

  if (data.length > 0 && Array.isArray(data[0]?.hits)) {
    return flattenSearchAllHits(response);
  }

  return data.map((hit) => normalizeHit(hit, fallbackContentType));
}

/**
 * Search a single Strapi content type.
 */
export async function searchContent({
  q,
  contentType,
  website,
  locale,
  limit = 20,
  offset = 0,
}) {
  const query = new URLSearchParams({
    q,
    contentType,
    limit: String(limit),
    offset: String(offset),
  });

  if (website) query.set("website", website);
  if (locale) query.set("locale", locale);

  const res = await fetch(`${apiBase}/search?${query}`, {
    headers: searchHeaders(),
  });

  return parseSearchResponse(res);
}

/**
 * Search all indexed content types for a website.
 */
export async function searchAll(q, website = "cas", locale = "en", limit = DEFAULT_SEARCH_LIMIT) {
  const query = new URLSearchParams({
    q,
    limit: String(limit),
  });

  if (website) query.set("website", website);
  if (locale) query.set("locale", locale);

  const res = await fetch(`${apiBase}/search/all?${query}`, {
    headers: searchHeaders(),
  });

  return parseSearchResponse(res);
}

/**
 * Flatten grouped search-all hits into a single list.
 */
export function flattenSearchAllHits(response) {
  const groups = response?.data;
  if (!Array.isArray(groups)) return [];

  return groups.flatMap((group) => {
    const hits = group?.hits;
    if (!Array.isArray(hits)) return [];
    return hits.map((hit) =>
      normalizeHit(hit, group.contentType || hit.contentType)
    );
  });
}

function dedupeHits(hits) {
  const seen = new Set();
  return hits.filter((hit) => {
    const key = hit.id || `${hit.documentId || ""}:${hit.url || ""}`;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getSearchHitCategoryPriority(hit) {
  const contentTypeUid = resolveHitContentTypeUid(hit, "");
  const category = resolveContentCategory(contentTypeUid || hit.contentType);
  if (category && category in SEARCH_CATEGORY_ORDER) {
    return SEARCH_CATEGORY_ORDER[category];
  }
  return 4;
}

function normalizeSearchText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Lower score = better match (exact wording first). */
function getSearchMatchPriority(hit, query) {
  const q = normalizeSearchText(query);
  if (!q) return 6;

  const title = normalizeSearchText(hit.title || hit.name);
  const searchableText = normalizeSearchText(hit.searchableText);

  if (title === q) return 0;
  if (title.startsWith(q)) return 1;

  const wordPattern = new RegExp(
    `(^|[\\s\\-_/])${escapeRegExp(q)}($|[\\s\\-_/])`,
    "i"
  );
  if (title && wordPattern.test(` ${title} `)) return 2;

  if (title.includes(q)) return 3;
  if (searchableText === q) return 4;
  if (searchableText.includes(q)) return 5;

  return 6;
}

function sortSearchHits(hits, query) {
  return [...hits].sort((a, b) => {
    const matchDiff = getSearchMatchPriority(a, query) - getSearchMatchPriority(b, query);
    if (matchDiff !== 0) return matchDiff;

    const categoryDiff =
      getSearchHitCategoryPriority(a) - getSearchHitCategoryPriority(b);
    if (categoryDiff !== 0) return categoryDiff;

    return String(a.title || "").localeCompare(String(b.title || ""), undefined, {
      sensitivity: "base",
    });
  });
}

async function finalizeSearchHits(hits, locale, limit, query) {
  const enriched = await enrichSearchHitUrls(dedupeHits(hits), locale);
  return sortSearchHits(enriched, query).slice(0, limit);
}

function isUsefulSearchAllResponse(response, website = "cas") {
  const groups = response?.data;
  if (!Array.isArray(groups) || groups.length === 0) {
    return false;
  }

  const flatHits = flattenSearchAllHits(response);
  if (flatHits.length > 0) {
    return true;
  }

  if (website !== "cas") {
    return false;
  }

  return groups.some(
    (group) =>
      group?.indexName === "cas" ||
      String(group?.contentType || "").includes(".cas-")
  );
}

/**
 * CAS website search.
 * Uses /api/search/all when it returns hits; otherwise falls back to per content-type search.
 */
export async function searchCasWebsite(q, locale = "en", limit = DEFAULT_SEARCH_LIMIT) {
  const trimmedQuery = q.trim();
  if (!trimmedQuery) return [];

  let allHits = [];
  let allResponse = null;

  try {
    allResponse = await searchAll(trimmedQuery, "cas", locale, limit);
    if (isUsefulSearchAllResponse(allResponse, "cas")) {
      allHits = flattenSearchAllHits(allResponse);
    }
  } catch {
    allHits = [];
  }

  if (allHits.length > 0) {
    return finalizeSearchHits(allHits, locale, limit, trimmedQuery);
  }

  const perTypeLimit = Math.min(FALLBACK_PER_TYPE_LIMIT, limit);
  const settled = await Promise.allSettled(
    CAS_SEARCH_CONTENT_TYPES.map(async (contentType) => {
      const response = await searchContent({
        q: trimmedQuery,
        contentType,
        website: "cas",
        locale,
        limit: perTypeLimit,
      });
      return normalizeSearchHits(response, contentType);
    })
  );

  const merged = [];
  let failureCount = 0;

  for (const result of settled) {
    if (result.status === "fulfilled") {
      merged.push(...result.value);
    } else {
      failureCount += 1;
    }
  }

  const enrichedHits = await finalizeSearchHits(merged, locale, limit, trimmedQuery);

  if (enrichedHits.length === 0 && failureCount === settled.length) {
    throw new Error("Search is unavailable right now. Please try again later.");
  }

  return enrichedHits;
}
